import { Routes } from '@angular/router';
import { AuthenticatedAndAuthorizedGuard } from './core/guards/authenticated.guard';
import { LoginConfigResolver } from './core/resolvers/company-login-config.resolver';
import { LoginEntryGuard } from './core/guards/login-entry.guard';
import { Constants } from './core/constants/constants';
import { OAuthTokenResolver } from './core/resolvers/oauth-token.resolver';
import { Saml2TokenResolver } from './core/resolvers/saml2-token.resolver';
import { UpdatePasswordResolver } from './core/resolvers/update-password.resolver';
import { MainLayoutComponent } from './core/layout/main-layout.component';
import { CompanyTestflightConfigResolver } from './core/resolvers/company-testflight-config.resolver';
import { WKAuthTokenResolver } from './core/resolvers/wkauth-token.resolver';
import { WKAuthSuccessComponent } from './features/wkauth-success/wkauth-success.component';

export const routes: Routes = [
    // --- ROUTES WITHOUT Main Layout
    {
        path: 'login',
        title: 'login.title',
        canActivate: [LoginEntryGuard],
        resolve: {
            companyLoginConfiguration: LoginConfigResolver
        },
        loadComponent: () => import('./features/login/login.component').then((m) => m.LoginComponent)
    },
    {
        path: 'oauth-success',
        title: 'home.welcome.title',
        resolve: {
            authCode: OAuthTokenResolver
        },
        loadComponent: () => import('./features/oauth-success/oauth-success.component').then((m) => m.OAuthSuccessComponent)
    },
    {
        path: 'saml2-success',
        title: 'home.welcome.title',
        resolve: {
            samlToken: Saml2TokenResolver
        },
        loadComponent: () => import('./features/saml2-success/saml2-success.component').then((m) => m.Saml2SuccessComponent)
    },
    {
        path: 'wkauth-success',
        title: 'users.syncTooltip',
        resolve: {
            authCode: WKAuthTokenResolver
        },
        loadComponent: () => import('./features/wkauth-success/wkauth-success.component').then((m) => m.WKAuthSuccessComponent)
    },
    {
        path: 'reset-password',
        title: 'login.form.resetpassword',
        canActivate: [LoginEntryGuard],
        loadComponent: () => import('./features/reset-password/reset-password.component').then((m) => m.ResetPasswordComponent)
    },
    {
        path: 'email-sent',
        title: 'email-sent.title',
        canActivate: [LoginEntryGuard],
        loadComponent: () => import('./features/email-sent/email-sent.component').then((m) => m.EmailSentComponent)
    },
    {
        path: 'activate-user',
        title: 'login.form.activateuser',
        canActivate: [LoginEntryGuard],
        resolve: {
            resolvedData: UpdatePasswordResolver
        },
        loadComponent: () => import('./features/update-password/update-password.component').then((m) => m.UpdatePasswordComponent)
    },
    {
        path: 'update-password',
        title: 'login.form.updatepassword',
        canActivate: [LoginEntryGuard],
        resolve: {
            resolvedData: UpdatePasswordResolver
        },
        loadComponent: () => import('./features/update-password/update-password.component').then((m) => m.UpdatePasswordComponent)
    },
    {
        path: 'privacy-policy',
        title: 'global.privacyPolicy',
        data: {
            template: 'privacy-policy.html',
            baseRoute: '/privacy-policy'
        },
        loadComponent: () => import('./features/legal-documents/legal-documents.component').then((m) => m.LegalDocumentsComponent)
    },
    {
        path: 'privacy-policy-en',
        title: 'global.privacyPolicy',
        data: {
            template: 'privacy-policy-en.html',
            baseRoute: '/privacy-policy'
        },
        loadComponent: () => import('./features/legal-documents/legal-documents.component').then((m) => m.LegalDocumentsComponent)
    },
    {
        path: 'privacy-policy-fr',
        title: 'global.privacyPolicy',
        data: {
            template: 'privacy-policy-fr.html',
            baseRoute: '/privacy-policy'
        },
        loadComponent: () => import('./features/legal-documents/legal-documents.component').then((m) => m.LegalDocumentsComponent)
    },
    {
        path: 'terms-and-conditions',
        title: 'global.termsAndConditions',
        data: {
            template: 'terms-and-conditions.html',
            baseRoute: '/terms-and-conditions'
        },
        loadComponent: () => import('./features/legal-documents/legal-documents.component').then((m) => m.LegalDocumentsComponent)
    },
    {
        path: 'terms-and-conditions-en',
        title: 'global.termsAndConditions',
        data: {
            template: 'terms-and-conditions-en.html',
            baseRoute: '/terms-and-conditions'
        },
        loadComponent: () => import('./features/legal-documents/legal-documents.component').then((m) => m.LegalDocumentsComponent)
    },
    {
        path: 'terms-and-conditions-fr',
        title: 'global.termsAndConditions',
        data: {
            template: 'terms-and-conditions-fr.html',
            baseRoute: '/terms-and-conditions'
        },
        loadComponent: () => import('./features/legal-documents/legal-documents.component').then((m) => m.LegalDocumentsComponent)
    },
    {
        path: 'personal-data-treatment',
        title: 'global.personalDataTreatment',
        data: {
            template: 'personal-data-treatment.html',
            baseRoute: '/personal-data-treatment'
        },
        loadComponent: () => import('./features/legal-documents/legal-documents.component').then((m) => m.LegalDocumentsComponent)
    },
    {
        path: 'personal-data-treatment-en',
        title: 'global.personalDataTreatment',
        data: {
            template: 'personal-data-treatment-en.html',
            baseRoute: '/personal-data-treatment'
        },
        loadComponent: () => import('./features/legal-documents/legal-documents.component').then((m) => m.LegalDocumentsComponent)
    },
    {
        path: 'personal-data-treatment-fr',
        title: 'global.personalDataTreatment',
        data: {
            template: 'personal-data-treatment-fr.html',
            baseRoute: '/personal-data-treatment'
        },
        loadComponent: () => import('./features/legal-documents/legal-documents.component').then((m) => m.LegalDocumentsComponent)
    },
    {
        path: 'install-via-testflight',
        title: 'testFlight.title',
        resolve: {
            testflightConfiguration: CompanyTestflightConfigResolver
        },
        loadComponent: () => import('./features/install-via-testflight/install-via-testflight.component').then((m) => m.InstallViaTestflightComponent)
    },
    {
        path: 'not-found',
        title: '404.title',
        loadComponent: () => import('./features/not-found/not-found.component').then((m) => m.NotFoundComponent)
    },
    // --- ROUTES WITH MAIN LAYOUT
    {
        path: '',
        component: MainLayoutComponent,
        canActivate: [AuthenticatedAndAuthorizedGuard],
        children: [
            {
                path: '', // Home
                title: 'home.welcome.title',
                // canActivate: [AuthenticatedAndAuthorizedGuard],
                data: {
                    roles: [Constants.USER_ROLES.admin, Constants.USER_ROLES.manager, Constants.USER_ROLES.publisher]
                },
                loadComponent: () => import('./features/home/home.component').then((m) => m.HomeComponent)
            },
            {
                path: 'profile',
                title: 'users.profile.title',
                // canActivate: [AuthenticatedAndAuthorizedGuard],
                data: {
                    roles: [
                        Constants.USER_ROLES.admin,
                        Constants.USER_ROLES.manager,
                        Constants.USER_ROLES.publisher,
                        Constants.USER_ROLES.ftrayResponsible,
                        Constants.USER_ROLES.documentsManager,
                        Constants.USER_ROLES.platformAdmin,
                        Constants.USER_ROLES.teamResponsible,
                        Constants.USER_ROLES.humanResources,
                        Constants.USER_ROLES.employeeManager
                    ]
                },
                loadComponent: () => import('./features/profile/profile.component').then((m) => m.ProfileComponent)
            },
            {
                path: 'messages',
                loadChildren: () => import('./features/messages/messages.routes').then((m) => m.MESSAGES_ROUTES)
            },
            {
                path: 'questionnaires',
                loadChildren: () => import('./features/questionnaires/questionnaires.routes').then((m) => m.QUESTIONNAIRES_ROUTES)
            },
            {
                path: 'knowledge-center',
                loadChildren: () => import('./features/knowledge-center/knowledge-center.routes').then((m) => m.MESSAGES_ROUTES)
            },
            {
                path: 'requests',
                title: 'global.navbar.conversations',
                loadComponent: () => import('./features/requests/requests.component').then((m) => m.RequestsComponent)
            },
            {
                path: 'users',
                loadChildren: () => import('./features/users/users.routes').then((m) => m.USERS_ROUTES)
            },
            {
                path: 'user',
                loadChildren: () => import('./features/users/user/user.routes').then((m) => m.USER_ROUTES)
            },
            {
                path: 'timeoff/requests',
                title: 'global.navbar.timeoffRequests',
                data: {
                    roles: [Constants.USER_ROLES.admin, Constants.USER_ROLES.humanResources],
                    module: Constants.MODULES.HOLIDAYS
                },
                loadComponent: () => import('./features/timeoff/timeoff.component').then((m) => m.TimeoffComponent)
            },
            {
                path: 'documents',
                loadChildren: () => import('./features/documents/documents.routes').then((m) => m.DOCUMENTS_ROUTES)
            },
            {
                path: 'remote-signature',
                loadChildren: () => import('./features/remote-signature/remote-signature.routes').then((m) => m.REMOTE_SIGNATURE_ROUTES)
            },
            {
                path: 'upload-documents',
                loadChildren: () => import('./features/upload-documents/upload-documents.routes').then((m) => m.UPLOAD_DOCUMENTS_ROUTES)
            },
            {
                path: 'time-tracking',
                loadChildren: () => import('./features/time-tracking/time-tracking.routes').then((m) => m.TIME_TRACKING_ROUTES)
            },
            {
                path: 'statistics',
                title: 'global.navbar.statistics',
                loadChildren: () => import('./features/statistics/statistics.routes').then((m) => m.STATISTICS_ROUTES)
            },
            {
                path: 'settings',
                loadChildren: () => import('./features/settings/settings.routes').then((m) => m.SETTINGS_ROUTES)
            },
            {
                path: 'audit',
                loadComponent: () => import('./features/audit/audit.component').then((m) => m.AuditComponent)
            },
            {
                path: 'forbidden',
                title: '403.title',
                loadComponent: () => import('./features/forbidden/forbidden.component').then((m) => m.ForbiddenComponent)
            },
            {
                path: 'server-error',
                title: '500.title',
                loadComponent: () => import('./features/server-error/server-error.component').then((m) => m.ServerErrorComponent)
            }
        ]
    },
    {
        path: '**',
        pathMatch: 'full',
        redirectTo: 'not-found'
    }
];
